import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Home as HomeIcon } from "lucide-react";
import { useDocumentMeta } from "../hooks/useDocumentMeta";

/**
 * Branded 404. Referenced from the catch-all route in AppRouter.
 *
 * Note on the HTTP status: the frontend is served as static assets so the
 * server always returns 200 for the SPA shell. To make Google (and monitoring)
 * see a real 404 we set a meta tag that our prerender/edge worker translates
 * into an actual 404 response header. See `/public/_headers` and the
 * Cloudflare Worker guidance in the README.
 */
export default function NotFound() {
  useDocumentMeta({
    title: "Not found · JJ Argent",
    description: "The page you're looking for doesn't exist. Try the homepage, case studies or the Recruiters page.",
    path: "/404",
    noindex: true,
  });

  React.useEffect(() => {
    // Signal to the prerender/edge worker to emit a 404 status header
    const m = document.createElement("meta");
    m.name = "prerender-status-code";
    m.content = "404";
    document.head.appendChild(m);
    return () => { m.remove(); };
  }, []);

  return (
    <main id="main" data-testid="not-found-page" className="min-h-[70vh] bg-[#0B0B0C]">
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <p className="eyebrow">404 · Page not found</p>
        <h1 className="mt-6 font-display text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-[#FAF9F6]">
          That page doesn't exist.
        </h1>
        <p className="mt-6 max-w-xl text-lg text-[#A9A9A6] leading-relaxed">
          You've hit a link that no longer resolves. Head back to the homepage or try one of the pages below.
        </p>
        <div className="mt-10 flex flex-wrap gap-3">
          <Link to="/" data-testid="notfound-home" className="btn-primary">
            <HomeIcon size={14} /> Homepage
          </Link>
          <Link to="/case-studies" data-testid="notfound-case-studies" className="btn-ghost">
            Case Studies <ArrowUpRight size={14} />
          </Link>
          <Link to="/for-recruiters" data-testid="notfound-recruiters" className="btn-ghost">
            For Recruiters <ArrowUpRight size={14} />
          </Link>
          <Link to="/contact" data-testid="notfound-contact" className="btn-ghost">
            Contact <ArrowUpRight size={14} />
          </Link>
        </div>
      </section>
    </main>
  );
}
